import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Calendar as CalendarIcon, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { formatDate, formatDateShort } from '../../utils/dateUtils';
import './CalendarDatePicker.css';

export interface CalendarDatePickerProps {
  /** Value as ISO date string (YYYY-MM-DD) or empty */
  value: string;
  onChange: (date: string) => void;
  minDate?: string;
  maxDate?: string;
  placeholder?: string;
  id?: string;
  /** Optional label shown above the trigger (e.g. "From", "To") */
  label?: string;
  className?: string;
  /** 'default' = "Feb 6, 2026", 'short' = "02/06/2026" (MM/DD/YYYY) */
  dateDisplayFormat?: 'default' | 'short';
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS_HEADER = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];

function toDate(iso: string): Date | null {
  if (!iso) return null;
  const d = new Date(iso + 'T12:00:00');
  return isNaN(d.getTime()) ? null : d;
}

function toISO(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function isSameDay(a: Date | null, b: Date | null): boolean {
  if (!a || !b) return false;
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function getCalendarGrid(year: number, month: number): { date: Date; isCurrentMonth: boolean }[] {
  const first = new Date(year, month, 1);
  const startDow = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const prevMonth = month === 0 ? 11 : month - 1;
  const prevYear = month === 0 ? year - 1 : year;
  const daysInPrevMonth = new Date(prevYear, prevMonth + 1, 0).getDate();

  const grid: { date: Date; isCurrentMonth: boolean }[] = [];
  // Leading days from previous month
  for (let i = 0; i < startDow; i++) {
    const d = daysInPrevMonth - startDow + 1 + i;
    grid.push({ date: new Date(prevYear, prevMonth, d), isCurrentMonth: false });
  }
  // Current month
  for (let d = 1; d <= daysInMonth; d++) {
    grid.push({ date: new Date(year, month, d), isCurrentMonth: true });
  }
  // Trailing days to complete 6 rows (42 cells)
  const remaining = 42 - grid.length;
  for (let d = 1; d <= remaining; d++) {
    grid.push({ date: new Date(year, month + 1, d), isCurrentMonth: false });
  }
  return grid;
}

export const CalendarDatePicker: React.FC<CalendarDatePickerProps> = ({
  value,
  onChange,
  minDate,
  maxDate,
  placeholder = 'Select date',
  id,
  label,
  className = '',
  dateDisplayFormat = 'default',
}) => {
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(() => {
    const d = value ? toDate(value) : new Date();
    return (d || new Date()).getFullYear();
  });
  const [viewMonth, setViewMonth] = useState(() => {
    const d = value ? toDate(value) : new Date();
    return (d || new Date()).getMonth();
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [popoverAlign, setPopoverAlign] = useState<'left' | 'right'>('left');
  const [popoverPosition, setPopoverPosition] = useState<{ top: number; left: number } | null>(null);

  const valueDate = toDate(value);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Position popover below trigger (for portal)
  const updatePopoverPosition = () => {
    const trigger = containerRef.current?.querySelector('.calendar-date-picker-trigger');
    if (!trigger) return;
    const rect = (trigger as HTMLElement).getBoundingClientRect();
    const popoverWidth = 280;
    const popoverHeight = 320;
    const gap = 8;
    let left = rect.left;
    if (rect.left + popoverWidth > window.innerWidth - 12) {
      left = window.innerWidth - popoverWidth - 12;
    } else if (rect.right - popoverWidth < 12) {
      left = 12;
    }
    let top = rect.bottom + gap;
    if (top + popoverHeight > window.innerHeight - 12) {
      top = Math.max(12, rect.top - popoverHeight - gap);
    }
    setPopoverPosition({ top, left });
  };

  useEffect(() => {
    if (open) {
      updatePopoverPosition();
      const onScrollOrResize = () => updatePopoverPosition();
      window.addEventListener('scroll', onScrollOrResize, true);
      window.addEventListener('resize', onScrollOrResize);
      return () => {
        window.removeEventListener('scroll', onScrollOrResize, true);
        window.removeEventListener('resize', onScrollOrResize);
      };
    } else {
      setPopoverPosition(null);
    }
  }, [open]);

  // Keep view month in sync when value changes and popover opens
  useEffect(() => {
    if (open && value) {
      const d = toDate(value);
      if (d) {
        setViewYear(d.getFullYear());
        setViewMonth(d.getMonth());
      }
    }
  }, [open, value]);

  // Close on outside click (trigger or popover)
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        containerRef.current?.contains(target) ||
        popoverRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const displayText = value && valueDate
    ? (dateDisplayFormat === 'short' ? formatDateShort(value) : formatDate(value))
    : placeholder;
  const grid = getCalendarGrid(viewYear, viewMonth);

  const goPrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else {
      setViewMonth((m) => m - 1);
    }
  };

  const goNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  const handleSelectDate = (d: Date) => {
    onChange(toISO(d));
    setOpen(false);
  };

  const handleClear = () => {
    onChange('');
    setOpen(false);
  };

  const handleToday = () => {
    onChange(toISO(today));
    setOpen(false);
  };

  const isDisabled = (d: Date): boolean => {
    const iso = toISO(d);
    if (minDate && iso < minDate) return true;
    if (maxDate && iso > maxDate) return true;
    return false;
  };

  return (
    <div className={`calendar-date-picker ${className}`.trim()} ref={containerRef}>
      {label && <label className="calendar-date-picker-label" htmlFor={id}>{label}</label>}
      <button
        type="button"
        id={id}
        className="calendar-date-picker-trigger"
        data-format={dateDisplayFormat}
        data-value={value || undefined}
        onClick={() => {
          if (!open) {
            const rect = containerRef.current?.getBoundingClientRect();
            const popoverWidth = 280;
            if (rect && window.innerWidth - rect.right < popoverWidth) {
              setPopoverAlign('right');
            } else {
              setPopoverAlign('left');
            }
          }
          setOpen((o) => !o);
        }}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={label ? `${label}: ${displayText}` : displayText}
      >
        <CalendarIcon size={18} strokeWidth={2} className="calendar-date-picker-trigger-icon" aria-hidden />
        <span className="calendar-date-picker-trigger-text">{displayText}</span>
      </button>

      {open && popoverPosition &&
        createPortal(
          <div
            ref={popoverRef}
            className={`calendar-date-picker-popover calendar-date-picker-popover-portal ${popoverAlign === 'right' ? 'calendar-date-picker-popover-right' : ''}`}
            role="dialog"
            aria-label="Choose date"
            style={{
              position: 'fixed',
              top: popoverPosition.top,
              left: popoverPosition.left,
              right: 'auto',
            }}
          >
          <div className="calendar-date-picker-header">
            <div className="calendar-date-picker-month-year">
              <span>{MONTHS[viewMonth]} {viewYear}</span>
              <ChevronDown size={16} strokeWidth={2} aria-hidden />
            </div>
            <div className="calendar-date-picker-nav">
              <button type="button" className="calendar-date-picker-nav-btn" onClick={goPrevMonth} aria-label="Previous month">
                <ChevronLeft size={18} strokeWidth={2} />
              </button>
              <button type="button" className="calendar-date-picker-nav-btn" onClick={goNextMonth} aria-label="Next month">
                <ChevronRight size={18} strokeWidth={2} />
              </button>
            </div>
          </div>

          <div className="calendar-date-picker-days-header">
            {DAYS_HEADER.map((day) => (
              <span key={day} className="calendar-date-picker-day-name">{day}</span>
            ))}
          </div>

          <div className="calendar-date-picker-grid">
            {grid.map(({ date, isCurrentMonth }, i) => {
              const selected = isSameDay(date, valueDate);
              const isTodayCell = isSameDay(date, today);
              const disabled = isDisabled(date);
              return (
                <button
                  key={i}
                  type="button"
                  className={`calendar-date-picker-cell ${!isCurrentMonth ? 'other-month' : ''} ${selected ? 'selected' : ''} ${isTodayCell ? 'today' : ''} ${disabled ? 'disabled' : ''}`}
                  onClick={() => !disabled && handleSelectDate(date)}
                  disabled={disabled}
                  aria-label={date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>

          <div className="calendar-date-picker-footer">
            <button type="button" className="calendar-date-picker-footer-clear" onClick={handleClear}>
              CLEAR
            </button>
            <button type="button" className="calendar-date-picker-footer-today" onClick={handleToday}>
              TODAY
            </button>
          </div>
        </div>,
          document.body
        )}
    </div>
  );
};
